import React from 'react';
import { faSortUp, faSortDown } from '@fortawesome/pro-solid-svg-icons';
import { ActionTableProps, Key } from './types';
import {
  StyledCheckbox,
  Toolbar,
  SelectAllBtn,
  StyledTd,
  StyledTh,
  SortIconsContainer,
  ArrowUpIcon,
  ArrowDownIcon,
  SortableCell,
  ActionTableWrapper,
  StyledInfoTable,
  CellWrapper,
} from './styles';
import { OL_COLOUR_BLACK_25, OL_COLOUR_NAVY_BABY } from '../Style/colours';

export const ActionTable: React.FC<ActionTableProps> = ({
  itemName,
  headers,
  rows,
  toolbarActions,
  selectionType = 'checkbox',
  selectedRows,
  selectAll,
  totalItems,
  sortBy,
  onSelectAllChanged,
  onSelectedRowsChanged,
  onSortChanged,
  ...infoTableProps
}) => {
  const currentPageSelected: { [key: string]: boolean } = {};
  let currentPageSelectedCount = 0;
  rows.forEach((row) => {
    const selected = selectedRows.findIndex((x) => x === row.key) > -1;
    currentPageSelected[row.key] = selected;
    currentPageSelectedCount += selected ? 1 : 0;
  });

  const handleRowCheckboxClicked = (key: Key): void => {
    if (currentPageSelected[key]) {
      // Remove from the list
      onSelectedRowsChanged(selectedRows.filter((x) => x !== key));
    } else {
      // Add to the list
      onSelectedRowsChanged([...selectedRows, key]);
    }
  };

  const handlePageCheckboxClicked = (): void => {
    if (currentPageSelectedCount === rows.length) {
      // Deselect all in current page
      onSelectedRowsChanged(
        selectedRows.filter((x) => currentPageSelected[x] === undefined)
      );
    } else {
      // Select all in current page
      const unselectedRows = rows
        .filter((x) => !currentPageSelected[x.key])
        .map((x) => x.key);
      onSelectedRowsChanged([...selectedRows, ...unselectedRows]);
    }
  };

  const handleSelectAllClicked = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    event.preventDefault();
    onSelectAllChanged(!selectAll);
    onSelectedRowsChanged([]);
  };

  const handleSortClicked = (sortKey?: string): void => {
    if (onSortChanged && sortKey) {
      if (sortBy && sortBy.sortKey === sortKey) {
        if (sortBy.direction === 'descending') {
          onSortChanged({ sortKey, direction: 'ascending' });
        } else {
          // Clear sorting
          onSortChanged();
        }
      } else {
        onSortChanged({ sortKey, direction: 'descending' });
      }
    }
  };

  if (rows.length === 0) {
    return <div>No {itemName}s found</div>;
  }

  return (
    <ActionTableWrapper>
      <Toolbar>
        <div>
          {selectAll ? (
            <div>
              {totalItems} out of {totalItems} selected
              <SelectAllBtn type="button" onClick={handleSelectAllClicked}>
                Unselect all
              </SelectAllBtn>
            </div>
          ) : selectedRows.length > 0 ? (
            <>
              <span>
                {selectedRows.length} out of {totalItems} selected
              </span>
              <SelectAllBtn onClick={handleSelectAllClicked}>
                Select all {totalItems}{' '}
                {totalItems > 1 ? `${itemName}s` : itemName}
              </SelectAllBtn>
            </>
          ) : (
            <div>
              {totalItems} {totalItems > 1 ? `${itemName}s` : itemName}
            </div>
          )}
        </div>
        {toolbarActions}
      </Toolbar>
      <StyledInfoTable {...infoTableProps}>
        <table>
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th key={header.key}>
                  <CellWrapper>
                    {index === 0 && selectionType === 'checkbox' && (
                      <StyledCheckbox
                        disabled={selectAll}
                        indeterminate={
                          currentPageSelectedCount > 0 &&
                          currentPageSelectedCount !== rows.length
                        }
                        checked={
                          selectAll || currentPageSelectedCount === rows.length
                        }
                        onChange={handlePageCheckboxClicked}
                      />
                    )}
                    {header.sortKey ? (
                      <SortableCell
                        type="button"
                        activated={sortBy && sortBy.sortKey === header.sortKey}
                        onClick={(
                          event: React.MouseEvent<HTMLButtonElement>
                        ) => {
                          event.preventDefault();
                          handleSortClicked(header.sortKey);
                        }}
                      >
                        {header.cell}
                        <SortIconsContainer>
                          <ArrowUpIcon
                            icon={faSortUp}
                            color={
                              sortBy &&
                              sortBy.sortKey === header.sortKey &&
                              sortBy.direction === 'ascending'
                                ? OL_COLOUR_NAVY_BABY
                                : OL_COLOUR_BLACK_25
                            }
                          />
                          <ArrowDownIcon
                            icon={faSortDown}
                            color={
                              sortBy &&
                              sortBy.sortKey === header.sortKey &&
                              sortBy.direction === 'descending'
                                ? OL_COLOUR_NAVY_BABY
                                : OL_COLOUR_BLACK_25
                            }
                          />
                        </SortIconsContainer>
                      </SortableCell>
                    ) : (
                      header.cell
                    )}
                  </CellWrapper>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                {row.cells.map((cell, index) => {
                  const CellComponent = index === 0 ? StyledTh : StyledTd;
                  return (
                    <CellComponent
                      key={headers[index].key}
                      selected={selectAll || currentPageSelected[row.key]}
                    >
                      <CellWrapper>
                        {index === 0 && selectionType === 'checkbox' && (
                          <StyledCheckbox
                            disabled={selectAll}
                            checked={selectAll || currentPageSelected[row.key]}
                            onChange={() => {
                              handleRowCheckboxClicked(row.key);
                            }}
                          />
                        )}
                        {cell.cell}
                      </CellWrapper>
                    </CellComponent>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </StyledInfoTable>
    </ActionTableWrapper>
  );
};
